<template>
  <div ref="target" class="relative">
    <button
      type="button"
      class="toolbar-toggle rte-tt"
      :aria-label="t('messages.editor.phrases')"
      :data-tt="t('messages.editor.phrases')"
      @click="showDropdown = !showDropdown"
    >
      <span class="pi pi-arrow-down text-xs"></span>
    </button>

    <div v-show="showDropdown" class="phrases-menu">
      <div class="phrases-items">
        <template v-if="userPhrases.length">
          <div class="phrases-section-header">
            {{ t('messages.myPhrases') }}
          </div>
          <div
            v-for="phrase in userPhrases"
            :key="`user-${phrase.id}`"
            class="phrases-item group flex items-center justify-between"
            @click="insert(phrase.label)"
          >
            <span class="mr-2 flex-1">{{ phrase.label }}</span>
            <button
              type="button"
              class="delete-phrase-btn opacity-0 group-hover:opacity-100"
              :title="t('messages.delete')"
              @click.stop="pools.deletePhrase(phrase)"
            >
              <span class="pi pi-times text-xs text-red-500"></span>
            </button>
          </div>
          <div v-if="options.length" class="phrases-divider"></div>
        </template>

        <template v-if="options.length">
          <div v-if="userPhrases.length" class="phrases-section-header">
            {{ t('messages.predefinedValues') }}
          </div>
          <div
            v-for="option in options"
            :key="option.value"
            class="phrases-item"
            @click="insert(option.label)"
          >
            {{ option.label }}
          </div>
        </template>

        <div v-if="!userPhrases.length && !options.length" class="phrases-empty-state">
          {{ t('messages.noPhrasesYet') }}
        </div>
      </div>

      <div v-if="textToSave && textToSave.trim()" class="phrases-footer">
        <button
          type="button"
          class="save-phrase-btn"
          :disabled="savingPhrase"
          @click.stop="pools.saveAsPhrase(textToSave)"
        >
          <span class="pi pi-bookmark mr-1"></span>
          {{ t('messages.saveAsPhrase') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onClickOutside } from '@vueuse/core'
import { onMounted, shallowRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePredefinedValues } from '@/composables/usePredefinedValues'

const props = defineProps({
  category: { type: String, required: true },
  name: { type: String, required: true },
  // Current editor plain text — offered as the "Salvesta fraasina" payload.
  textToSave: { type: String, default: '' },
  // Optional shared usePredefinedValues instance (the editor's bubble-menu
  // save-selection writes into the same list this dropdown renders).
  sharedPools: { type: Object, default: null },
})

const emit = defineEmits(['insert'])

const { t } = useI18n()

const pools =
  props.sharedPools ??
  usePredefinedValues(
    () => props.category,
    () => props.name,
  )
const { options, userPhrases, savingPhrase } = pools

const target = useTemplateRef('target')
const showDropdown = shallowRef(false)

onClickOutside(target, () => {
  showDropdown.value = false
})

onMounted(() => {
  pools.loadOptions()
  pools.loadUserPhrases()
})

const insert = label => {
  emit('insert', label)
  showDropdown.value = false
}
</script>

<style scoped>
  @reference '../../../css/tailwind.css';

  /* Same visual language as PredefinedValuesInputComponent's dropdown. */
  .toolbar-toggle {
    @apply flex h-7 w-7 cursor-pointer items-center justify-center rounded-sm border-none bg-transparent text-surface-600 dark:text-surface-300;

    &:hover {
      @apply bg-surface-100 dark:bg-surface-700;
    }
  }

  .phrases-menu {
    min-width: 18rem;
    max-width: 24rem;
    right: 0;
    padding: 0;
    position: absolute;
    z-index: 1000;
    background-color: #fff;
    @apply rounded-md shadow-md dark:bg-gray-800 dark:text-white;
  }

  .phrases-items {
    max-height: 300px;
    overflow-y: auto;
    padding: 0.5rem 0;

    .phrases-item {
      white-space: normal;
      word-wrap: break-word;
      padding: 0.5rem 1rem;
      line-height: 1.4;
      cursor: pointer;
      font-size: 0.875rem;

      &:hover {
        background-color: #f8f9fa;
        @apply dark:bg-gray-700;
      }
    }
  }

  .phrases-section-header {
    padding: 0.25rem 1rem;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    color: #6b7280;
    letter-spacing: 0.05em;
    @apply dark:text-gray-400;
  }

  .phrases-divider {
    height: 1px;
    margin: 0.25rem 0;
    background-color: #e5e7eb;
    @apply dark:bg-gray-600;
  }

  .phrases-empty-state {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: #6b7280;
    font-style: italic;
    @apply dark:text-gray-400;
  }

  .phrases-footer {
    border-top: 1px solid #e5e7eb;
    padding: 0.5rem;
    @apply dark:border-gray-600;
  }

  .save-phrase-btn {
    width: 100%;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    color: #3b82f6;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: left;
    border-radius: 0.25rem;
    display: flex;
    align-items: center;

    &:hover {
      background-color: #eff6ff;
      @apply dark:bg-gray-700;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .delete-phrase-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 0.25rem;
    transition: opacity 0.15s ease-in-out;

    &:hover {
      background-color: #fee2e2;
      @apply dark:bg-red-900/30;
    }
  }
</style>
